import React, {ReactElement} from 'react';
import {cx} from '@emotion/css';
import Field from '@fo/designSystem/Fields/Field/Field';
import {Field as FieldType} from '@fo/designSystem/Fields/types';

import formatDisplayValue from '@fo/designSystem/Fields/NumberField/formatDisplayValue';
import changeHandler from '@fo/designSystem/Fields/NumberField/changeHandlerFactory';
import {CURRENCY_SYMBOL} from '@fo/shared-types/constants';

import fieldStyles from '@fo/designSystem/Fields/Field/Field.styles';
import styles from './Currency.styles';

export interface Props extends Omit<FieldType, 'value' | 'onChange'> {
  maximumValue?: number;
  minimumValue?: number;
  required?: boolean;
  onChange: (name: string, value: string | number) => void;
  value: string | number;
  testId?: string;
  tooltip?: string;
  maxLength?: number;
}

function Currency({
  assistiveMessage = '',
  errorMessage = '',
  label,
  maximumValue,
  minimumValue,
  name,
  testId,
  tooltip,
  onChange,
  onBlur = () => {
    /* noop */
  },
  placeholder = '',
  value,
  warningMessage = '',
  required = false,
  isDisabled,
  showOptionalMessage,
  maxLength,
  className,
  isReadOnly,
}: Props): ReactElement {
  const numberToDisplay = formatDisplayValue(value);
  const handleChange = changeHandler(name, onChange, {maximumValue});

  const hasWarning = !!warningMessage;
  const hasError = !!errorMessage;

  return (
    <Field
      assistiveMessage={assistiveMessage}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      label={label}
      tooltip={tooltip}
      name={name}
      showOptionalMessage={showOptionalMessage}
      isDisabled={isDisabled}
      className={className}
    >
      <div className={cx(styles.currency(isDisabled))} data-currency-symbol={CURRENCY_SYMBOL}>
        <input
          className={cx(fieldStyles.input(hasWarning, hasError, isDisabled))}
          aria-label={label}
          id={name}
          max={maximumValue}
          min={minimumValue}
          name={name}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          type="text"
          pattern="[0-9]*"
          inputMode="numeric"
          spellCheck="false"
          value={numberToDisplay}
          required={required}
          disabled={isDisabled}
          readOnly={isReadOnly}
          data-testid={testId}
          maxLength={maxLength}
        />
      </div>
    </Field>
  );
}

export default Currency;
